import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Calculator, ShieldCheck, Activity } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758691463620-188ca7c1a04f?crop=entropy&cs=srgb&fm=jpg&q=85";

const LINES = [
  { text: "US-Based Virtual", accent: false },
  { text: "Medical Assistants.", accent: true },
  { text: "Fractional Cost.", accent: false },
  { text: "Unmatched Support.", accent: false },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-32 -right-40 h-[480px] w-[480px] rounded-full bg-[#E6F4EE]" />
      <div className="pointer-events-none absolute top-64 -left-48 h-[380px] w-[380px] rounded-full bg-[#F3F2EB]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="inline-flex items-center gap-2.5 rounded-full border border-[#0F1E19]/10 bg-white px-4 py-2"
            data-testid="hero-cohort-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#10B981]" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" />
            </span>
            <span className="font-mono text-xs font-medium text-[#0A3B2C]">
              Active VMA Cohort: 48 Ready for Placement
            </span>
          </motion.div>

          <h1
            className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.08] tracking-tight text-[#0F1E19]"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-[#0A3B2C]" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#4A5D55]"
            data-testid="hero-subtext"
          >
            Eliminate practice burnout, cut administrative overhead by 70%, and streamline patient
            care with HIPAA-trained, highly skilled virtual MAs available immediately.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-consultation-btn"
              onClick={() => scrollToSection("#contact")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#0A3B2C] px-7 py-3.5 text-sm font-semibold text-[#FAF9F5] transition-all hover:bg-[#072B20] hover:shadow-xl"
            >
              Book a Free Consultation
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-savings-calculator-btn"
              onClick={() => scrollToSection("#savings")}
              className="group inline-flex items-center gap-2 rounded-full border border-[#0A3B2C]/25 bg-white px-7 py-3.5 text-sm font-semibold text-[#0A3B2C] transition-all hover:border-[#0A3B2C] hover:shadow-lg"
            >
              <Calculator className="h-4 w-4" />
              Calculate Practice Savings
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2"
            data-testid="hero-trust-row"
          >
            {["HIPAA Compliant", "Epic & AthenaHealth Certified", "48-Hour Onboarding"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#4A5D55]">
                <ShieldCheck className="h-3.5 w-3.5 text-[#059669]" />
                {t}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="overflow-hidden rounded-[2rem] border-4 border-white shadow-2xl">
                <img
                  src={HERO_IMG}
                  alt="Virtual medical assistant supporting a US clinic"
                  className="h-[420px] sm:h-[500px] w-full object-cover"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-[#0F1E19]/10" />
            </motion.div>

            <motion.div
              style={{ y: cardY }}
              className="absolute -left-4 sm:-left-10 bottom-10 rounded-2xl bg-[#0A261E] px-5 py-4 shadow-2xl"
              data-testid="hero-live-shift-card"
            >
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-[#10B981]" />
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3B8B0]">Live shift</p>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-[#F5F7F6]">Prior Auth · Apex Family Medicine</p>
              <p className="mt-0.5 font-mono text-xs text-[#10B981]">On schedule · 14 auths completed</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.3 }}
              className="absolute -right-3 sm:-right-6 -top-6 rounded-2xl bg-white px-5 py-4 shadow-xl border border-[#E2E0D5]"
              data-testid="hero-savings-chip"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A5D55]">Admin cost cut</p>
              <p className="font-display text-3xl font-extrabold text-[#0A3B2C]">70%</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
